import algoliasearch from "algoliasearch";
import { getAlgoliaAgent } from "instantsearch.js/es/lib/utils/index.js";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, AbstractChat } from "ai";
import { handleHistoricalProductSearchToolCall, HISTORICAL_PRODUCT_SEARCH_TOOL_NAME } from "./historical-product-search-tool.js";
import { handleRfqCategoryToolCall, RFQ_CATEGORY_TOOL_NAME } from "./rfq-category-tool.js";

class ChatState {
  constructor() {
    this._messages = [];
    this._status = "ready";
    this._error = undefined;
    this._listeners = { messages: new Set(), status: new Set(), error: new Set() };
  }

  get messages() { return this._messages; }
  set messages(v) { this._messages = [...v]; this._notify("messages"); }

  get status() { return this._status; }
  set status(v) { this._status = v; this._notify("status"); }

  get error() { return this._error; }
  set error(v) { this._error = v; this._notify("error"); }

  pushMessage(m) { this._messages = [...this._messages, m]; this._notify("messages"); }
  popMessage() { this._messages = this._messages.slice(0, -1); this._notify("messages"); }
  replaceMessage(i, m) {
    this._messages = [...this._messages.slice(0, i), JSON.parse(JSON.stringify(m)), ...this._messages.slice(i + 1)];
    this._notify("messages");
  }
  snapshot(x) { return JSON.parse(JSON.stringify(x)); }

  _notify(type) { this._listeners[type].forEach(cb => cb()); }

  ["~registerMessagesCallback"](cb) { this._listeners.messages.add(cb); return () => this._listeners.messages.delete(cb); }
  ["~registerStatusCallback"](cb) { this._listeners.status.add(cb); return () => this._listeners.status.delete(cb); }
  ["~registerErrorCallback"](cb) { this._listeners.error.add(cb); return () => this._listeners.error.delete(cb); }
}

class CleanTransport {
  constructor(inner) { this._inner = inner; }
  sendMessages(payload, opts) {
    if (Array.isArray(payload?.messages)) {
      payload = {
        ...payload,
        messages: payload.messages.map(m => {
          if (m.role !== "assistant" || !m.parts) return m;
          return { ...m, parts: m.parts.filter(p => !p.type?.startsWith("data-")) };
        }),
      };
    }
    return this._inner.sendMessages(payload, opts);
  }
}

function isToolPart(part) {
  return part?.type === "dynamic-tool" || part?.type?.startsWith("tool-");
}

// Agent Studio's cached replays put every server tool call and the final answer
// in one step, so the SDK predicate alone resubmits a finished answer (HTTP 422).
// Only continue while the assistant message still ends on its tool calls.
export function shouldContinueAfterToolCalls({ messages }) {
  if (!lastAssistantMessageIsCompleteWithToolCalls({ messages })) return false;
  const parts = messages[messages.length - 1].parts;
  const lastToolIndex = parts.findLastIndex(isToolPart);
  return !parts.slice(lastToolIndex + 1).some(part => part.type === "text" && part.text?.trim());
}

const CLIENT_TOOL_NAMES = new Set([HISTORICAL_PRODUCT_SEARCH_TOOL_NAME, RFQ_CATEGORY_TOOL_NAME]);

function getToolPartName(part) {
  return part?.type === "dynamic-tool" ? part.toolName : part?.type?.slice("tool-".length);
}

// True while the assistant message ends on a browser-executed tool call, so the SDK
// will send a follow-up request once the tool output is added.
export function isAwaitingToolContinuation(messages) {
  const message = messages?.[messages.length - 1];
  if (message?.role !== "assistant" || !Array.isArray(message.parts)) return false;
  const lastToolIndex = message.parts.findLastIndex(isToolPart);
  if (lastToolIndex < 0) return false;
  if (!CLIENT_TOOL_NAMES.has(getToolPartName(message.parts[lastToolIndex]))) return false;
  return !message.parts.slice(lastToolIndex + 1).some(part => part.type === "text" && part.text?.trim());
}

let lastCompletionStatus = 0;

// "rate_limit" when Agent Studio or its AI provider reports a usage or rate limit; otherwise "error".
export function classifyChatError(error, status = 0) {
  if (status === 429) return "rate_limit";
  const text = String(error?.message || error || "");
  return /rate[ _-]?limit|quota|too many requests|insufficient_quota|\b429\b/i.test(text) ? "rate_limit" : "error";
}

let chat = null;
let state = null;
let activeRequestId = null;
let configuredChatId = null;
const requestErrorListeners = new Set();

function getState() {
  if (!state) state = new ChatState();
  return state;
}

function generateMessageId() {
  return crypto.randomUUID?.() || `msg_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getConfig() {
  const appId = window.algolia?.application_id;
  const apiKey = window.algolia?.search_api_key;
  const agentId = window.algolia?.agent_id;
  if (!appId || !apiKey || !agentId) {
    throw new Error("Missing Algolia configuration (appId / apiKey / agentId).");
  }
  return { appId, apiKey, agentId };
}

function initChat() {
  if (chat) return chat;

  const { appId, apiKey, agentId } = getConfig();
  const searchClient = algoliasearch(appId, apiKey);
  const userAgent = getAlgoliaAgent(searchClient);

  const headers = {
    "x-algolia-application-id": appId,
    "x-algolia-api-key": apiKey,
    "x-algolia-agent": userAgent,
  };

  const userToken = window.algolia?.user_info?.user_token;
  if (userToken) headers["x-algolia-user-token"] = userToken;

  const secureUserToken = window.algolia?.secure_user_token;
  if (secureUserToken) headers["X-Algolia-Secure-User-Token"] = secureUserToken;

  const rawTransport = new DefaultChatTransport({
    api: `https://${appId}.algolia.net/agent-studio/1/agents/${agentId}/completions?stream=true&compatibilityMode=ai-sdk-5`,
    headers,
    // Remember the last HTTP status so errors can be classified (e.g. 429 usage limits).
    fetch: async (...args) => {
      const response = await globalThis.fetch(...args);
      lastCompletionStatus = response.status;
      return response;
    },
  });

  state = getState();

  chat = new AbstractChat({
    id: configuredChatId || undefined,
    transport: new CleanTransport(rawTransport),
    state,
    generateId: generateMessageId,
    onToolCall: ({ toolCall }) => {
      // AbstractChat serializes stream processing and addToolOutput(). Awaiting
      // here would keep the tool-output job queued behind the active stream job.
      const handler = toolCall.toolName === RFQ_CATEGORY_TOOL_NAME
        ? handleRfqCategoryToolCall({
          toolCall,
          endpointUrl: window.algolia?.rfqCategoriesUrl,
          addToolOutput: output => chat.addToolOutput(output),
        })
        : handleHistoricalProductSearchToolCall({
          toolCall,
          endpointUrl: window.algolia?.historicalProductSearchUrl,
          addToolOutput: output => chat.addToolOutput(output),
        });

      void handler.catch(() => undefined);
    },
    sendAutomaticallyWhen: shouldContinueAfterToolCalls,
    onError: (error) => {
      console.error("Chat error:", error);
      const event = { requestId: activeRequestId, error, kind: classifyChatError(error, lastCompletionStatus) };
      requestErrorListeners.forEach(callback => callback(event));
    },
  });

  return chat;
}

export async function sendMessage(text, requestId) {
  const message = String(text || "").trim();
  if (!message) return;
  if (!requestId) throw new Error("A Charli request id is required.");
  const c = initChat();
  activeRequestId = requestId;
  try {
    await c.sendMessage({ text: message });
  } finally {
    if (activeRequestId === requestId) activeRequestId = null;
  }
}

export function getMessages() {
  return getState().messages;
}

export function hydrateMessages(messages) {
  if (!Array.isArray(messages)) return false;
  getState().messages = JSON.parse(JSON.stringify(messages));
  return true;
}

export function getStatus() {
  return getState().status;
}

export function configureChatId(chatId) {
  const normalizedChatId = typeof chatId === "string" ? chatId.trim() : "";
  if (!normalizedChatId) return false;
  const currentChatId = chat?.id || configuredChatId;
  if (currentChatId && currentChatId !== normalizedChatId) return false;
  configuredChatId = normalizedChatId;
  return true;
}

export function getChatId() {
  return chat?.id || configuredChatId || null;
}

// The Algolia conversation ID: Agent Studio uses the chat ID sent with each request
// (returned as x-conversation-id). Creates the chat if needed so the ID is final.
export function getConversationId() {
  try {
    return initChat().id || null;
  } catch {
    return getChatId();
  }
}

export function onMessagesUpdated(callback) {
  return getState()["~registerMessagesCallback"](callback);
}

export function onStatusUpdated(callback) {
  return getState()["~registerStatusCallback"](callback);
}

export function onError(callback) {
  requestErrorListeners.add(callback);
  return () => requestErrorListeners.delete(callback);
}

export function appendAssistantMessage(text, replaceMessageId = null, outcome = null) {
  const message = String(text || "").trim();
  if (!message) return null;
  const currentState = getState();
  const replaceIndex = replaceMessageId
    ? currentState.messages.findIndex(item => item.id === replaceMessageId && item.role === "assistant")
    : -1;
  const id = replaceIndex >= 0 ? replaceMessageId : generateMessageId();
  const assistantMessage = {
    id,
    role: "assistant",
    parts: [{ type: "text", text: message }],
  };
  if (outcome) assistantMessage.metadata = { charliOutcome: outcome };
  if (replaceIndex >= 0) currentState.replaceMessage(replaceIndex, assistantMessage);
  else currentState.pushMessage(assistantMessage);
  return id;
}

export function stop() {
  // Reset UI state on stop.
  try {
    chat?.stop();
  } finally {
    if (state) state.status = "ready";
  }
}
